import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Star, Send, Briefcase, TrendingUp, Lightbulb, Loader2, FileText } from "lucide-react";
import { Layout } from "@/components/Layout";
import { AvailabilityBadge } from "@/components/AvailabilityBadge";
import { Progress } from "@/components/ui/progress";
import { api, useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="gjt-card p-5">
    <div className="flex items-center justify-between">
      <Icon className="h-5 w-5 text-brand-blue" />
      <span className="font-display text-2xl font-extrabold text-navy">{value ?? "—"}</span>
    </div>
    <p className="mt-1 text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
  </div>
);

export default function CandidateDashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [tips, setTips] = useState([]);
  const [apps, setApps] = useState([]);

  useEffect(() => {
    api.get("/candidates/me/dashboard").then((r) => setD(r.data)).catch(() => {});
    api.get("/candidates/me/tips").then((r) => setTips(r.data.tips)).catch(() => {});
    api.get("/applications/mine").then((r) => setApps(r.data.results)).catch(() => {});
  }, []);

  if (!d) return <Layout><div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-brand-blue" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-extrabold text-navy">Good day, {user?.name?.split(" ")[0]}</h1>
            <div className="mt-2 flex items-center gap-3">
              <AvailabilityBadge status={d.availability} size="lg" />
              <Link to="/profile" className="text-sm font-semibold text-brand-blue hover:underline">Update profile →</Link>
            </div>
          </div>
          <Link to="/jobs" className="gjt-btn-orange rounded-xl px-6 py-3 text-sm font-bold self-start">Find jobs →</Link>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat icon={TrendingUp} label={t("home_rank")} value={d.home_rank ? `#${d.home_rank}` : "—"} testid="stat-homerank" />
          <Stat icon={Briefcase} label="Jobs matched" value={d.jobs_matched} testid="stat-matched" />
          <Stat icon={Eye} label="Employer views" value={d.views} testid="stat-views" />
          <Stat icon={Send} label={t("applications")} value={d.applications} testid="stat-applications" />
          <Stat icon={Star} label={t("shortlisted")} value={d.shortlists} testid="stat-shortlists" />
          <Stat icon={Send} label={t("invited")} value={d.invitations} testid="stat-invitations" />
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-3">
          <div className="gjt-card p-6 lg:col-span-1">
            <h3 className="font-display font-bold text-navy">{t("profile_strength")}</h3>
            <div className="mt-3 flex items-end justify-between">
              <span className="font-display text-4xl font-extrabold text-emerald-600">{d.completeness}%</span>
            </div>
            <Progress value={d.completeness} className="mt-3 h-2" />
            <div className="mt-5">
              <p className="flex items-center gap-2 text-sm font-semibold text-navy"><Lightbulb className="h-4 w-4 text-brand-orange" /> Improve your ranking</p>
              <ul className="mt-2 space-y-2 text-sm text-slate-600">
                {tips.map((tip, i) => <li key={i} data-testid={`tip-${i}`} className="flex gap-2">• {tip}</li>)}
              </ul>
            </div>
          </div>

          <div className="gjt-card p-6 lg:col-span-2">
            <h3 className="font-display font-bold text-navy flex items-center gap-2"><FileText className="h-4 w-4" /> My applications</h3>
            {apps.length === 0 ? (
              <p className="mt-6 text-center text-sm text-slate-400">No applications yet. <Link to="/jobs" className="font-semibold text-brand-blue">Browse jobs →</Link></p>
            ) : (
              <div className="mt-4 divide-y divide-slate-100">
                {apps.map((a) => (
                  <div key={a.id} data-testid={`application-${a.id}`} className="flex items-center justify-between py-3">
                    <div>
                      <p className="font-semibold text-navy">{a.job?.title || "Job"}</p>
                      <p className="text-xs text-slate-500">{a.job?.company_name} · {a.job?.city}</p>
                    </div>
                    <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold capitalize text-brand-blue">{a.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
